"use client"

import { useCallback, useState } from "react"
import Tree from "react-d3-tree"

const ROICNode = ({ title, companies, dataKey, format = "percent", className = "", isHighlighted = false }) => {
  // データキーに基づいて各企業のデータを取得
  const getCompanyValue = (company) => {
    const keys = dataKey.split(".")
    let value = company.data

    for (const key of keys) {
      if (!value[key]) return 0
      value = value[key]
    }
    
    return value
  }

  // 値のフォーマット
  const formatValue = (value) => {
    if (format === "percent") {
      return value.toFixed(2) + "%"
    } else if (format === "decimal") {
      return value.toFixed(2) + "倍"
    }
    return value.toFixed(2)
  }

  return (
    <div
      className={`
      border rounded-lg p-3 bg-white shadow-sm
      ${isHighlighted ? "border-primary border-2" : "border-gray-200"}
      ${className}
    `}
    >
      <div className="text-sm font-medium text-gray-600 mb-2">{title}</div>
      <div className="space-y-1">
        {companies.map((company) => {
          const value = getCompanyValue(company)
          if (value === null) return null

          return (
            <div key={company.id} className="flex justify-between text-sm">
              <span className="font-medium">{company.name.replace('株式会社', "")}:</span>
              <span className={`${isHighlighted ? "text-primary font-bold" : ""}`}>{formatValue(value)}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// カスタムノードコンポーネント
const CustomNode = ({ nodeDatum, companies }) => {
  return (
    <foreignObject width={180} height={120} x={-90} y={-60}>
      <ROICNode
        title={nodeDatum.name}
        companies={companies}
        dataKey={nodeDatum.dataKey}
        format={nodeDatum.format || "percent"}
        isHighlighted={nodeDatum.isHighlighted}
        className="w-full"
      />
    </foreignObject>
  )
}

export default function ROICTree({ companies }) {
  const [translate, setTranslate] = useState({ x: 200, y: 400 })

  // ROICデータをツリー構造に変換
  const treeData = {
    name: "のれんを含むROIC",
    dataKey: "roic",
    isHighlighted: true,
    children: [
      {
        name: "のれんを含まないROIC",
        dataKey: "roic_wo_goodwill",
        children: [
          {
            name: "税引き前ROIC",
            dataKey: "roic_before_tax",
            children: [
              {
                name: "EBITDA/売上高",
                dataKey: "ebitda_margin",
                format: "percent",
                children: [
                  {
                    name: "売上原価/売上高",
                    dataKey: "cost_of_sales_per_sales",
                    format: "percent",
                    children: [],
                  },
                  {
                    name: "販管費/売上高",
                    dataKey: "selling_general_and_administrative_expenses_per_sales",
                    format: "percent",
                    children: [],
                  },
                  {
                    name: "その他営業費用/売上高",
                    dataKey: "other_expenses_per_sales",
                    format: "percent",
                    children: [],
                  },
                ],
              },
              {
                name: "売上高/投下資本",
                dataKey: "sales_per_investment_capital",
                format: "decimal",
                children: [
                  {
                    name: "運転資本/売上高",
                    dataKey: "operating_assets_per_sales",
                    format: "percent",
                    children: [],
                  },
                  {
                    name: "固定資産/売上高",
                    dataKey: "non_current_assets_per_sales",
                    format: "percent",
                    children: [],
                  },
                  {
                    name: "その他資産/売上高",
                    dataKey: "other_assets_per_sales",
                    format: "percent",
                    children: [],
                  },
                ],
              },
            ],
          },
          {
            name: "税率",
            dataKey: "tax",
            children: [],
          },
        ],
      },
      {
        name: "事業プレミアム",
        dataKey: "acquisition_premium",
        children: [],
      },
    ],
  }

  // コンテナサイズに応じて位置を調整
  const containerRef = useCallback((containerElem) => {
    if (containerElem !== null) {
      const { width, height } = containerElem.getBoundingClientRect()
      setTranslate({ x: width / 4, y: height / 2 })
    }
  }, [])

  return (
    <div className="w-full h-[800px]" ref={containerRef}>
      <Tree
        data={treeData}
        orientation="horizontal"
        translate={translate}
        nodeSize={{ x: 240, y: 120 }}
        separation={{ siblings: 1, nonSiblings: 1.2 }}
        pathFunc="step"
        renderCustomNodeElement={(rd) => <CustomNode {...rd} companies={companies} />}
      />
    </div>
  )
}

